/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Box, Grid, Skeleton, useTheme } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useLazyGetDocumentAppIdQuery, usePostDocumentGenarateMutation } from "@api/documents";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@store/store";
import { setLoanStatus, setLoanApplication } from "@store/slices/registerSlice";
import {
  setRepresentanteLegal,
  setAval,
  setIsLoading,
  setBofuTabsObj,
  setBofuStatusAll,
  setAvalConyuge,
  setAvalRegimenConyugal,
  setAvalesAdicionales,
} from "@store/slices/bofuSlice";
import {
  useLazyGetLoanApplicationByIdQuery,
  usePostValidateNextStageMutation,
  usePutApplicationCompletedMutation,
} from "@api/applications";
import { useLazyGetPersonsQuery, usePatchPersonMutation } from "@api/personasApi";
import {
  DocumentResponse,
  BofuTabObject,
  BofuTabDocument,
  BofuTabStatus,
  BofuTabPropietario,
} from "@interfaces/response";
import { DOCUMENTS_CATALOG, STATUS_CATALOG, BOFU_TABS_CONFIG, TAX_SYSTEM_TYPE } from "@helpers/constantes";
import {
  useLazyGetCivilStatusQuery,
  useLazyGetGuaranteeTypeQuery,
  useLazyGetMatrimonialRegimesQuery,
  useLazyGetTabsConfigQuery,
} from "@api/catalogs";
import { identifyPersons } from "@helpers/funciones";
import TabBofuButton from "@components/TabBofu/TabBofuButton";
import { UUID } from "crypto";
import {
  generateTabLabel,
  generateDocumentConfig,
  identityDocumentsByPerson,
  validateDocumentStructure,
  validateRequiredDocumentsAreUploaded,
  filterDocumentsByGuarantee,
  generateGuaranteeDocs,
  generateGuaranteeTitle,
  generatePropietarioDocs,
} from "./functions";
import * as Sentry from "@sentry/react";
import { GuaranteeType } from "@interfaces/catalogs";
import { useLazyGetGuaranteesDataQuery } from "@api/garantiasApi";

const FORM_STATUS_COMPLETED = ["VALIDADO", "COMPLETO"];

const TabBofu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const [isSuccessData, setIsSuccessData] = useState<boolean>(false);

  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const applicationData = useSelector((state: RootState) => state.register.application);
  const personAval = useSelector((state: RootState) => state.bofu.aval);
  const personConyugeAval = useSelector((state: RootState) => state.bofu.aval_conyuge);
  const bofuTabsObj = useSelector((state: RootState) => state.bofu.bofu_tabs_obj);
  const isLoading = useSelector((state: RootState) => state.bofu.isLoading);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [getDocumentsByAppId] = useLazyGetDocumentAppIdQuery();
  const [getPersons] = useLazyGetPersonsQuery();
  const [getLoanAplication] = useLazyGetLoanApplicationByIdQuery();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getTabsConfig] = useLazyGetTabsConfigQuery();
  const [createDocAnexo1, createDocAnexo1Metadata] = usePostDocumentGenarateMutation();
  const [createDocAnexo2, createDocAnexo2Metadata] = usePostDocumentGenarateMutation();
  const [createDocAnexo1Conyuge, createDocAnexo1ConyugeMetadata] = usePostDocumentGenarateMutation();
  const [createDocSolicitudCredito, createDocSolicitudCreditoMetadata] = usePostDocumentGenarateMutation();
  const [putAapplicationCompleted] = usePutApplicationCompletedMutation();
  const [patchPerson] = usePatchPersonMutation();
  const [getGuaranteeType] = useLazyGetGuaranteeTypeQuery();
  const [getGuaranteesData] = useLazyGetGuaranteesDataQuery();
  const [getEstadoCivil] = useLazyGetCivilStatusQuery();
  const [getMatrimonialRegimen] = useLazyGetMatrimonialRegimesQuery();

  const generateAnexo1 = async () => {
    try {
      if (!bofuTabsObj.aval) {
        return;
      }

      if (!personAval) {
        return;
      }

      const { doc_anexo_1 } = bofuTabsObj.aval ?? {};

      const all_forms_completed =
        FORM_STATUS_COMPLETED.includes(personAval.personal_information_status || "") &&
        FORM_STATUS_COMPLETED.includes(personAval.bank_references_status || "")
          ? true
          : false;

      // descartar si no se han completado todos los formularios
      if (!all_forms_completed) {
        return;
      }

      // descartar si ya se ha validado
      if (doc_anexo_1?.validated === null || doc_anexo_1?.validated === true) {
        return;
      }

      // descartar si hay request en curso
      if (createDocAnexo1Metadata.isLoading) {
        return;
      }

      const payload_anexo_1 = {
        loan_application_id: applicationData.id,
        document_type: "anexo_1",
        person_id: personAval.id,
      };

      await createDocAnexo1(payload_anexo_1).unwrap();

      const payload_person = {
        completed: true,
      };

      await patchPerson({
        personId: personAval.id,
        body: payload_person,
      }).unwrap();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "TabBofu-generateAnexo1",
        },
      });
    }
  };

  const generateAnexo1ConyugeAval = async () => {
    try {
      if (!bofuTabsObj.aval) {
        return;
      }
      if (!personConyugeAval) {
        return;
      }
      const { doc_anexo_1_conyuge } = bofuTabsObj.aval ?? {};
      const all_forms_completed = FORM_STATUS_COMPLETED.includes(personConyugeAval.personal_information_status || "")
        ? true
        : false;

      // descartar si no se han completado todos los formularios
      if (!all_forms_completed) {
        return;
      }
      // descartar si ya se ha validado
      if (doc_anexo_1_conyuge?.validated === null || doc_anexo_1_conyuge?.validated === true) {
        return;
      }
      // descartar si hay request en curso
      if (createDocAnexo1ConyugeMetadata.isLoading) {
        return;
      }
      const payload_anexo_1 = {
        loan_application_id: applicationData.id,
        document_type: "anexo_1",
        person_id: personConyugeAval.id,
      };

      await createDocAnexo1Conyuge(payload_anexo_1).unwrap();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "TabBofu-generateAnexo1ConyugeAval",
        },
      });
    }
  };

  const generateAnexo2 = async () => {
    try {
      if (!bofuTabsObj.propietarios_reales || !bofuTabsObj.propietarios_reales.is_active) {
        return;
      }

      if (!bofuTabsObj.propietarios_reales.propietarios.length) {
        return;
      }

      if (createDocAnexo2Metadata.isLoading) {
        return;
      }

      const { propietarios } = bofuTabsObj.propietarios_reales;
      const promise_list = [];

      for (const propietario of propietarios) {
        if (FORM_STATUS_COMPLETED.includes(propietario.persona.personal_information_status || "")) {
          if (!propietario.doc_anexo_2 || propietario.doc_anexo_2.validated === false) {
            const payload_anexo_2 = {
              loan_application_id: applicationData.id,
              document_type: "anexo_2",
              person_id: propietario.persona.id,
            };
            promise_list.push(createDocAnexo2(payload_anexo_2).unwrap());
          }
        }
      }

      await Promise.all(promise_list);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "TabBofu-generateAnexo2",
        },
      });
    }
  };

  const generateSolicitudCredito = async () => {
    try {
      if (!bofuTabsObj.empresa) {
        return;
      }

      const { doc_solicitud_credito } = bofuTabsObj.empresa ?? {};

      const all_forms_completed =
        applicationData.loan_application_information_status === "COMPLETO" &&
        applicationData.references_status === "COMPLETO" &&
        applicationData.bank_information_status === "COMPLETO";

      // descartar si no se han completado todos los formularios
      if (!all_forms_completed) {
        return;
      }

      // descartar si ya se ha validado
      if (doc_solicitud_credito?.validated === true || doc_solicitud_credito?.validated === null) {
        return;
      }

      // descartar si hay request en curso
      if (createDocSolicitudCreditoMetadata.isLoading) {
        return;
      }

      const payload_document = {
        loan_application_id: applicationData.id,
        document_type: "solicitud_credito",
      };
      await createDocSolicitudCredito(payload_document).unwrap();

      const payload_loan = {
        completed: true,
      };
      await putAapplicationCompleted({ id_sol: applicationData.id, body: payload_loan });
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "TabBofu-create_solicitud_credito",
        },
      });
    }
  };

  const sendToDictamenJuridico = async () => {
    try {
      const { documentos_dictamen, dictamen_juridico } = STATUS_CATALOG;

      const required_documents_uploaded = validateRequiredDocumentsAreUploaded(bofuTabsObj.dictamen.documents);
      if (!required_documents_uploaded) {
        return;
      }

      if (applicationData.status_catalog?.code !== documentos_dictamen.code) {
        return;
      }

      await triggerPostNextStage({
        applicationId: applicationData.id,
        statusCode: dictamen_juridico.code,
        username: `BOFU - ${accountUser.email}`,
      }).unwrap();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "TabBofu-sendToDictamenJuridico",
        },
      });
    }
  };

  const generateFictitiousDocument = (partialDocument: Partial<DocumentResponse>): DocumentResponse => {
    const fictitious_document: DocumentResponse = {
      id: crypto.randomUUID(),
      validated: null,
      ...partialDocument,
    } as unknown as DocumentResponse;
    return fictitious_document;
  };

  const generateTabsStatusLabel = () => {
    let dictamen_new_status: BofuTabStatus | undefined;
    let empresa_new_status: BofuTabStatus | undefined;
    let aval_new_status: BofuTabStatus | undefined;
    let propietario_real_new_status: BofuTabStatus | undefined;
    let garantia_new_status: BofuTabStatus | undefined;
    let representante_new_status: BofuTabStatus | undefined;

    if (bofuTabsObj.dictamen.is_active) {
      const params_dictamen_status = {
        tab_documents: bofuTabsObj.dictamen.documents,
        other_documents: [],
      };
      dictamen_new_status = generateTabLabel(params_dictamen_status);
    }

    if (bofuTabsObj.empresa.is_active) {
      let document_information: DocumentResponse | undefined = undefined;
      let document_references: DocumentResponse | undefined = undefined;
      let document_bank_information: DocumentResponse | undefined = undefined;

      // generar el documento de informacion de la solicitud
      if (applicationData.loan_application_information_status === "VALIDADO") {
        document_information = generateFictitiousDocument({
          validated: true,
          file_name: "informacion.pdf",
        });
      } else if (applicationData.loan_application_information_status === "INCIDENCIA") {
        document_information = generateFictitiousDocument({
          validated: false,
          file_name: "informacion.pdf",
        });
      } else if (applicationData.loan_application_information_status === "COMPLETO") {
        document_information = generateFictitiousDocument({ file_name: "informacion.pdf" });
      } else {
        document_information = undefined;
      }

      // generar el documento de referencias
      if (applicationData.references_status === "VALIDADO") {
        document_references = generateFictitiousDocument({
          validated: true,
          file_name: "referencias.pdf",
        });
      } else if (applicationData.references_status === "INCIDENCIA") {
        document_references = generateFictitiousDocument({
          validated: false,
          file_name: "referencias.pdf",
        });
      } else if (applicationData.references_status === "COMPLETO") {
        document_references = generateFictitiousDocument({ file_name: "referencias.pdf" });
      } else {
        document_references = undefined;
      }

      // generar el documento de informacion bancaria
      if (applicationData.bank_information_status === "VALIDADO") {
        document_bank_information = generateFictitiousDocument({
          validated: true,
          file_name: "informacion_bancaria.pdf",
        });
      } else if (applicationData.bank_information_status === "INCIDENCIA") {
        document_bank_information = generateFictitiousDocument({
          validated: false,
          file_name: "informacion_bancaria.pdf",
        });
      } else if (applicationData.bank_information_status === "COMPLETO") {
        document_bank_information = generateFictitiousDocument({
          file_name: "informacion_bancaria.pdf",
        });
      } else {
        document_bank_information = undefined;
      }

      const params_empresa_status = {
        tab_documents: bofuTabsObj.empresa.documents,
        other_documents: [document_information, document_references, document_bank_information],
      };

      empresa_new_status = generateTabLabel(params_empresa_status);
    }

    if (bofuTabsObj.aval.is_active) {
      const params_aval_status = {
        tab_documents: [] as BofuTabDocument[],
        other_documents: [] as (DocumentResponse | undefined)[],
      };

      if (bofuTabsObj.aval.show_form_aval) {
        // agregar los documentos del aval
        bofuTabsObj.aval.documents.forEach((item) => {
          params_aval_status.tab_documents.push(item);
        });

        let document_information: DocumentResponse | undefined = undefined;
        let document_bank_information: DocumentResponse | undefined = undefined;

        if (personAval?.personal_information_status === "VALIDADO") {
          document_information = generateFictitiousDocument({ validated: true });
        } else if (personAval?.personal_information_status === "INCIDENCIA") {
          document_information = generateFictitiousDocument({ validated: false });
        } else if (personAval?.personal_information_status === "COMPLETO") {
          document_information = generateFictitiousDocument({});
        } else {
          document_information = undefined;
        }

        if (personAval?.bank_references_status === "VALIDADO") {
          document_bank_information = generateFictitiousDocument({ validated: true });
        } else if (personAval?.bank_references_status === "INCIDENCIA") {
          document_bank_information = generateFictitiousDocument({ validated: false });
        } else if (personAval?.bank_references_status === "COMPLETO") {
          document_bank_information = generateFictitiousDocument({});
        } else {
          document_bank_information = undefined;
        }

        params_aval_status.other_documents.push(document_information);
        params_aval_status.other_documents.push(document_bank_information);

        // agregar validacion de identidad
        params_aval_status.other_documents.push(bofuTabsObj.aval.doc_validacion_identidad);
      }

      if (personConyugeAval) {
        bofuTabsObj.aval.documents_conyuge.forEach((item) => {
          params_aval_status.tab_documents.push(item);
        });

        let document_information: DocumentResponse | undefined = undefined;

        if (personConyugeAval.personal_information_status === "VALIDADO") {
          document_information = generateFictitiousDocument({ validated: true });
        } else if (personConyugeAval.personal_information_status === "INCIDENCIA") {
          document_information = generateFictitiousDocument({ validated: false });
        } else if (personConyugeAval.personal_information_status === "COMPLETO") {
          document_information = generateFictitiousDocument({});
        } else {
          document_information = undefined;
        }

        params_aval_status.other_documents.push(document_information);
      }

      aval_new_status = generateTabLabel(params_aval_status);
    }

    if (bofuTabsObj.propietarios_reales.is_active) {
      if (applicationData.decision_validated) {
        if (bofuTabsObj.propietarios_reales.propietarios.length) {
          const params_propietarios_reales_status = {
            tab_documents: bofuTabsObj.propietarios_reales.propietarios.flatMap(
              (item: BofuTabPropietario) => item.documents,
            ),
            other_documents: [] as (DocumentResponse | undefined)[],
          };

          bofuTabsObj.propietarios_reales.propietarios.forEach((item) => {
            let document_information: DocumentResponse | undefined = undefined;
            if (item.persona.personal_information_status === "VALIDADO") {
              document_information = generateFictitiousDocument({ validated: true });
            } else if (item.persona.personal_information_status === "INCIDENCIA") {
              document_information = generateFictitiousDocument({ validated: false });
            } else if (item.persona.personal_information_status === "COMPLETO") {
              document_information = generateFictitiousDocument({});
            } else {
              document_information = undefined;
            }

            params_propietarios_reales_status.other_documents.push(document_information);
          });

          propietario_real_new_status = generateTabLabel(params_propietarios_reales_status);
        } else {
          propietario_real_new_status = "no_aplica";
        }
      } else {
        propietario_real_new_status = undefined;
      }
    }

    if (bofuTabsObj.garantia.is_active) {
      if (bofuTabsObj.garantia.items.length) {
        const params_garantia_status = {
          tab_documents: bofuTabsObj.garantia.items.flatMap((item) => item.documents),
          other_documents: [],
        };
        garantia_new_status = generateTabLabel(params_garantia_status);
      } else {
        garantia_new_status = "no_aplica";
      }
    }

    if (bofuTabsObj.representantes.is_active) {
      if (applicationData.decision_validated) {
        const params_representantes_status = {
          tab_documents: bofuTabsObj.representantes.items.flatMap((item) => item.documents),
          other_documents: [] as (DocumentResponse | undefined)[],
        };

        bofuTabsObj.representantes.items.forEach((item) => {
          if (item.show_information_form) {
            let document_information: DocumentResponse | undefined = undefined;

            if (item.persona.personal_information_status === "VALIDADO") {
              document_information = generateFictitiousDocument({ validated: true });
            } else if (item.persona.personal_information_status === "INCIDENCIA") {
              document_information = generateFictitiousDocument({ validated: false });
            } else if (item.persona.personal_information_status === "COMPLETO") {
              document_information = generateFictitiousDocument({});
            } else {
              document_information = undefined;
            }

            params_representantes_status.other_documents.push(document_information);
          }

          params_representantes_status.other_documents.push(item.doc_validacion_identidad);
        });

        representante_new_status = generateTabLabel(params_representantes_status);
      } else {
        representante_new_status = undefined;
      }
    }

    dispatch(
      setBofuStatusAll({
        status_dictamen: dictamen_new_status,
        status_empresa: empresa_new_status,
        status_aval: aval_new_status,
        status_propietarios_reales: propietario_real_new_status,
        status_garantia: garantia_new_status,
        status_representante: representante_new_status,
      }),
    );
  };

  const generateTabsStructure = async () => {
    try {
      dispatch(setIsLoading(true));
      setIsSuccessData(false);

      const [
        tabs_config,
        loan_application,
        persons,
        documents,
        list_estado_civil,
        list_matrimonial_regimen,
        guarantee_type_list,
        guarantee_data,
      ] = await Promise.all([
        getTabsConfig({ taxcode: applicationData.type }).unwrap(),
        getLoanAplication(applicationData.id).unwrap(),
        getPersons({
          loanApplicationId: applicationData.id,
        }).unwrap(),
        getDocumentsByAppId({
          loanApplicationId: applicationData.id,
          status: true,
        }).unwrap(),
        getEstadoCivil().unwrap(),
        getMatrimonialRegimen().unwrap(),
        getGuaranteeType().unwrap(),
        getGuaranteesData({ loan_application_id: applicationData.id }).unwrap(),
      ]);

      // identificar si es una solicitud pm o pfae
      const isPFAE = loan_application?.tax_systems?.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code;

      // validar si la solicitud fue rechazada por documentacion
      const { rechazo_expediente } = STATUS_CATALOG;
      if (loan_application?.status_catalog?.code === rechazo_expediente.code) {
        dispatch(setLoanStatus(rechazo_expediente.code));
        navigate(rechazo_expediente.url);
        return;
      }

      // identificar las personas, todo: falta propietarios
      const {
        representante,
        representantes,
        aval,
        conyuge_aval,
        propietarios,
        segundo_aval,
        segundos_avales,
        aval_matrimonial_regimen,
      } = identifyPersons(persons, list_estado_civil, list_matrimonial_regimen);

      // obtener los documentos por persona
      const { docs_account, docs_representante, docs_aval, docs_conyuge_aval, docs_segundo_aval, docs_guarantee } =
        identityDocumentsByPerson({
          documents,
          representante,
          aval,
          conyuge_aval,
          segundo_aval,
          client_details: clientDetails,
        });

      // obtener los documentos de los propietarios
      const propietarios_ids = propietarios.map((item) => item.id);
      const representantes_ids = representantes.map((item) => item.id);
      const docs_propietarios = documents.filter((item) => propietarios_ids.includes(item.person_id as UUID));
      const docs_representantes = documents.filter((item) => representantes_ids.includes(item.person_id as UUID));

      // identificar el tipo de garantia, en caso de ser necesario
      const guarantee_type: GuaranteeType | undefined = loan_application.guarantee_type_id
        ? guarantee_type_list.find((item) => item.id === loan_application.guarantee_type_id)
        : undefined;

      const { solicitud_credito, validacion_identidad, anexo_1, anexo_2 } = DOCUMENTS_CATALOG;

      const bofu_tabs_obj: BofuTabObject = {
        dictamen: {
          code: BOFU_TABS_CONFIG.tab_dictamen.code,
          url: BOFU_TABS_CONFIG.tab_dictamen.url,
          order: 1,
          is_active: false,
          is_disabled: false,
          tab_status: undefined,
          documents: [],
        },
        empresa: {
          code: BOFU_TABS_CONFIG.tab_empresa.code,
          url: BOFU_TABS_CONFIG.tab_empresa.url,
          order: 2,
          is_active: false,
          is_disabled: false,
          tab_status: undefined,
          documents: [],
          doc_solicitud_credito: undefined,
        },
        aval: {
          code: BOFU_TABS_CONFIG.tab_aval.code,
          url: BOFU_TABS_CONFIG.tab_aval.url,
          order: 3,
          is_active: false,
          is_disabled: false,
          tab_status: undefined,
          documents: [],
          documents_conyuge: [],
          doc_anexo_1: undefined,
          doc_validacion_identidad: undefined,
          show_form_aval: true,
          doc_anexo_1_conyuge: undefined,
          doc_validacion_identidad_conyuge: undefined,
          show_information_form_conyuge: false,
        },
        propietarios_reales: {
          code: BOFU_TABS_CONFIG.tab_propietario.code,
          url: BOFU_TABS_CONFIG.tab_propietario.url,
          order: 4,
          is_active: false,
          is_disabled: false,
          tab_status: undefined,
          documents: [],
          propietarios: [],
        },
        garantia: {
          code: BOFU_TABS_CONFIG.tab_garantia.code,
          url: BOFU_TABS_CONFIG.tab_garantia.url,
          order: 5,
          is_active: false,
          is_disabled: false,
          tab_status: undefined,
          documents: [],
          items: [],
        },
        representantes: {
          code: BOFU_TABS_CONFIG.tab_representante.code,
          url: BOFU_TABS_CONFIG.tab_representante.url,
          order: 6,
          is_active: false,
          is_disabled: false,
          tab_status: undefined,
          documents: [],
          items: [],
        },
      };

      tabs_config.forEach((tabItem) => {
        // validar que los documentos tengan la estructura correcta
        let valid_documents: BofuTabDocument[] = [];
        tabItem.documents
          .filter((item) => validateDocumentStructure(item))
          .filter((item) => filterDocumentsByGuarantee(item, guarantee_type)) // filtrar los documentos por el tipo de garantia, si es que se requiere
          .forEach((docItem) => {
            const document_value = generateDocumentConfig({
              docItem,
              docs_account,
              docs_representante,
              docs_aval,
              docs_conyuge_aval,
              docs_segundo_aval,
              clientDetailsId: clientDetails.id,
              representante,
              aval,
              conyuge_aval,
              segundo_aval,
            });

            valid_documents.push(document_value);
          });

        // ordernar los documentos por el order
        valid_documents.sort((a, b) => (a.order || 0) - (b.order || 0));

        if (tabItem.tabs_bofu.code === BOFU_TABS_CONFIG.tab_dictamen.code) {
          bofu_tabs_obj.dictamen.order = tabItem.order;
          bofu_tabs_obj.dictamen.is_active = true;
          bofu_tabs_obj.dictamen.documents = valid_documents;
          bofu_tabs_obj.dictamen.is_disabled = false;

          if (isPFAE) {
            // pfae no tiene dictamen...
            bofu_tabs_obj.dictamen.is_active = false;
          }
        } else if (tabItem.tabs_bofu.code === BOFU_TABS_CONFIG.tab_empresa.code) {
          const doc_solicitud_credito = docs_account.find((item) => item.file_code === solicitud_credito.code);
          bofu_tabs_obj.empresa.order = tabItem.order;
          bofu_tabs_obj.empresa.is_active = true;
          bofu_tabs_obj.empresa.documents = valid_documents;
          bofu_tabs_obj.empresa.doc_solicitud_credito = doc_solicitud_credito;
          bofu_tabs_obj.empresa.is_disabled = false;

          if (isPFAE) {
            bofu_tabs_obj.empresa.url = "/producto/creditosimple/avance_expediente_digitalPFAE";
          }
        } else if (tabItem.tabs_bofu.code === BOFU_TABS_CONFIG.tab_aval.code) {
          // buscar los documentos requeridos
          const doc_anexo_1 = docs_aval.find((item) => item.file_code === anexo_1.code);
          const doc_validacion_identidad = docs_aval.find((item) => item.file_code === validacion_identidad.code);
          const doc_anexo_1_conyuge = docs_conyuge_aval.find((item) => item.file_code === anexo_1.code);
          const doc_validacion_identidad_conyuge = docs_conyuge_aval.find(
            (item) => item.file_code === validacion_identidad.code,
          );

          // si no hay conyuge del aval, se debe mostrar solo los documentos del aval
          if (!conyuge_aval) {
            valid_documents = valid_documents.filter((item) => item.relationship === "aval");
          }

          bofu_tabs_obj.aval.order = tabItem.order;
          bofu_tabs_obj.aval.is_active = true;
          bofu_tabs_obj.aval.is_disabled = loan_application.decision_validated ? false : true;
          bofu_tabs_obj.aval.documents = valid_documents.filter((item) => item.relationship === "aval");
          bofu_tabs_obj.aval.documents_conyuge = valid_documents.filter((item) => item.relationship === "conyuge_aval");
          bofu_tabs_obj.aval.doc_anexo_1 = doc_anexo_1;
          bofu_tabs_obj.aval.doc_validacion_identidad = doc_validacion_identidad;
          bofu_tabs_obj.aval.doc_anexo_1_conyuge = doc_anexo_1_conyuge;
          bofu_tabs_obj.aval.doc_validacion_identidad_conyuge = doc_validacion_identidad_conyuge;
          bofu_tabs_obj.aval.show_information_form_conyuge = conyuge_aval ? true : false;
          if (isPFAE) {
            bofu_tabs_obj.aval.is_disabled = false;
            bofu_tabs_obj.aval.show_form_aval = false;

            // si no hay conyuge del aval y no hay segundo aval, se debe desactivar el tab
            if (!conyuge_aval && !segundos_avales.length) {
              bofu_tabs_obj.aval.is_active = false;
            }
          }
        } else if (tabItem.tabs_bofu.code === BOFU_TABS_CONFIG.tab_propietario.code) {
          bofu_tabs_obj.propietarios_reales.order = tabItem.order;
          bofu_tabs_obj.propietarios_reales.is_active = true;
          bofu_tabs_obj.propietarios_reales.is_disabled = loan_application.decision_validated ? false : true;
          bofu_tabs_obj.propietarios_reales.propietarios = [];

          propietarios.forEach((propietarioItem) => {
            const propietario_documents = generatePropietarioDocs({
              propietarioItem,
              valid_documents,
              documents_uploaded: docs_propietarios,
              clientDetailsId: clientDetails.id,
            });

            // buscar el anexo 2 del propietario
            const doc_anexo_2 = docs_propietarios.find(
              (item) => item.file_code === anexo_2.code && item.person_id === propietarioItem.id,
            );

            bofu_tabs_obj.propietarios_reales.propietarios.push({
              persona: propietarioItem,
              doc_anexo_2: doc_anexo_2,
              documents: propietario_documents,
              show_information_form: true,
            });
          });

          if (isPFAE) {
            // pfae no tiene propietarios reales...
            bofu_tabs_obj.propietarios_reales.is_active = false;
          }
        } else if (tabItem.tabs_bofu.code === BOFU_TABS_CONFIG.tab_garantia.code) {
          bofu_tabs_obj.garantia.order = tabItem.order;
          bofu_tabs_obj.garantia.is_active = true;
          bofu_tabs_obj.garantia.is_disabled = false;
          bofu_tabs_obj.garantia.items = [];

          guarantee_data.forEach((garantiaItem, garantiaIndex) => {
            const guarantee_title = generateGuaranteeTitle({
              guarantee_item: garantiaItem,
              guarantee_index: garantiaIndex,
            });
            const guarantee_documents = generateGuaranteeDocs({
              clientDetailsId: clientDetails.id,
              guaranteeItem: garantiaItem,
              valid_documents: valid_documents,
              documents_uploaded: docs_guarantee,
            });

            bofu_tabs_obj.garantia.items.push({
              title: guarantee_title,
              guarantee: garantiaItem,
              documents: guarantee_documents,
            });
          });

          bofu_tabs_obj.garantia.items.sort((a, b) => a.title.localeCompare(b.title));
        } else if (tabItem.tabs_bofu.code === BOFU_TABS_CONFIG.tab_representante.code) {
          bofu_tabs_obj.representantes.order = tabItem.order;
          bofu_tabs_obj.representantes.is_active = true;
          bofu_tabs_obj.representantes.documents = valid_documents;
          bofu_tabs_obj.representantes.items = [];

          if (isPFAE) {
            // pfae no tiene dictamen, el RL se crea en el tofu
            bofu_tabs_obj.representantes.is_disabled = false;
          } else {
            bofu_tabs_obj.representantes.is_disabled = loan_application.decision_validated ? false : true;
          }

          representantes.forEach((representanteItem) => {
            const representante_documents = generatePropietarioDocs({
              propietarioItem: representanteItem,
              valid_documents,
              documents_uploaded: docs_representantes,
              clientDetailsId: clientDetails.id,
            });

            const doc_validacion_identidad = docs_representantes.find(
              (item) => item.file_code === validacion_identidad.code && item.person_id === representanteItem.id,
            );

            bofu_tabs_obj.representantes.items.push({
              persona: representanteItem,
              documents: representante_documents,
              show_information_form: true,
              doc_validacion_identidad,
            });
          });
        }
      });

      // mostrar u ocultar formularios segun la jerarquia:
      // si el RL es un aval o propietario real no se debe mostrar el formulario
      if (bofu_tabs_obj.representantes.is_active) {
        bofu_tabs_obj.representantes.items.forEach((representanteItem) => {
          const is_aval = aval?.id === representanteItem.persona.id;
          const is_propietario = propietarios.some(
            (propietarioItem) => propietarioItem.id === representanteItem.persona.id,
          );
          representanteItem.show_information_form = is_aval || is_propietario ? false : true;
        });
      }

      // si el PR es un aval no se debe mostrar el formulario
      if (bofu_tabs_obj.propietarios_reales.is_active) {
        bofu_tabs_obj.propietarios_reales.propietarios.forEach((propietarioItem) => {
          const is_aval = aval?.id === propietarioItem.persona.id;
          propietarioItem.show_information_form = is_aval ? false : true;
        });
      }

      // si el conyuge del aval es un propietario real no se debe mostrar el formulario
      if (conyuge_aval) {
        const is_propietario = propietarios.some((propietarioItem) => propietarioItem.id === conyuge_aval.id);
        bofu_tabs_obj.aval.show_information_form_conyuge = is_propietario ? false : true;
      }

      dispatch(setBofuTabsObj(bofu_tabs_obj));
      dispatch(setLoanApplication(loan_application));
      dispatch(setRepresentanteLegal(representante));
      dispatch(setAval(aval));
      dispatch(setAvalesAdicionales(segundos_avales));
      dispatch(setAvalConyuge(conyuge_aval));
      dispatch(setAvalRegimenConyugal(aval_matrimonial_regimen));
      setIsSuccessData(true);
      dispatch(setIsLoading(false));
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "TabBofu-generateTabsStructure",
        },
      });
    } finally {
      // dispatch(setIsLoading(false));
    }
  };

  useEffect(() => {
    generateTabsStatusLabel();
    sendToDictamenJuridico();
  }, [
    bofuTabsObj.dictamen.documents,
    bofuTabsObj.empresa.documents,
    bofuTabsObj.aval.documents,
    bofuTabsObj.aval.documents_conyuge,
    bofuTabsObj.propietarios_reales.propietarios,
    bofuTabsObj.garantia.items,
    bofuTabsObj.representantes.items,
    applicationData.decision_validated,
  ]);

  useEffect(() => {
    generateTabsStructure();
  }, [location.pathname]);

  useEffect(() => {
    generateSolicitudCredito();
    generateAnexo1();
    generateAnexo2();
    generateAnexo1ConyugeAval();
  }, [isLoading, bofuTabsObj, applicationData, personAval, personConyugeAval]);

  return (
    <>
      {isSuccessData ? (
        <Box
          sx={{
            width: "100%",
            height: "80px",
            bgcolor: theme?.palette?.backgroundRight?.tab,
            p: 1,
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Grid container spacing={1} display="flex" justifyContent="center">
            {Object.values(bofuTabsObj)
              .filter((tab) => tab.is_active)
              .sort((a, b) => a.order - b.order)
              .map((tab) => (
                <Grid item xs={2} md={2} key={tab.code}>
                  <TabBofuButton key={tab.code} bofuTab={tab} />
                </Grid>
              ))}
          </Grid>
        </Box>
      ) : (
        <Skeleton variant="rectangular" sx={{ height: "90px", width: "100%", borderRadius: 2 }} />
      )}
    </>
  );
};

export default TabBofu;
